import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import IosShareIcon from '@mui/icons-material/IosShare';
import CheckIcon from '@mui/icons-material/Check';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';

const EXPIRY = [
  [7, '7 дней', '7 days'],
  [30, '30 дней', '30 days'],
  [90, '3 месяца', '3 months'],
  [365, 'год', 'a year'],
  [0, 'без срока', 'no expiry'],
];

const api = async (url, opts = {}) => {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.message || `HTTP ${res.status}`);
  return j;
};

const fmt = (d) =>
  d
    ? new Date(d).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

/* «Поделиться оценкой»: a public link /a/<token> to this assessment's results
   and (optionally) its videos — for a parent or a colleague, no login. The
   links made before are listed with their views; each can be switched off. */
export default function ShareAssessmentDialog({ open, onClose, profile, en }) {
  const pid = profile && profile._id;
  const [links, setLinks] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState('');
  const [withVideo, setWithVideo] = useState(true);
  const [picked, setPicked] = useState([]);
  const [fullName, setFullName] = useState(false);
  const [comments, setComments] = useState(true);
  const [days, setDays] = useState(30);
  const [fresh, setFresh] = useState(null);
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    setError('');
    try {
      const [l, v] = await Promise.all([
        api(`/api/profiles/${pid}/shares`),
        api(`/api/profiles/${pid}/videos`).catch(() => []),
      ]);
      setLinks(l);
      setVideos(Array.isArray(v) ? v : []);
      setPicked((Array.isArray(v) ? v : []).map((x) => String(x._id)));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [pid]);

  useEffect(() => {
    if (open) {
      setFresh(null);
      setLabel('');
      load();
    }
  }, [open, load]);

  const abs = (url) => `${window.location.origin}${url}`;
  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(abs(url));
      setCopied(url);
      setTimeout(() => setCopied(''), 1800);
    } catch (e) {
      window.prompt(en ? 'Copy the link' : 'Скопируйте ссылку', abs(url));
    }
  };
  const shareOut = async (url) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: en ? 'Assessment results' : 'Результаты проверки',
          url: abs(url),
        });
        return;
      } catch (e) {
        if (e && e.name === 'AbortError') return;
      }
    }
    copy(url);
  };

  const create = async () => {
    setBusy(true);
    setError('');
    try {
      const all = picked.length === videos.length;
      const link = await api(`/api/profiles/${pid}/shares`, {
        method: 'POST',
        body: JSON.stringify({
          label,
          videos: !withVideo || !videos.length ? 'none' : all ? 'all' : 'some',
          assetIds: picked,
          fullName,
          comments,
          days,
        }),
      });
      setFresh(link);
      setLinks((l) => [link, ...l]);
      setLabel('');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      const v = await api(`/api/profiles/${pid}/shares/${id}`, {
        method: 'DELETE',
      });
      setLinks((l) => l.map((x) => (x._id === id ? v : x)));
      if (fresh && fresh._id === id) setFresh(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const active = links.filter((l) => l.status === 'on');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>
        {en ? 'Share the assessment' : 'Поделиться оценкой'}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', mb: 2 }}>
          {en
            ? 'Anyone with the link sees the results of this assessment (and the videos you choose) without logging in. You can switch the link off at any time.'
            : 'По ссылке результаты этой оценки (и выбранные видео) видны без входа — родителю, коллеге, методисту. Ссылку можно выключить в любой момент.'}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {fresh && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 3,
              bgcolor: (t) => alpha(t.palette.success.main, 0.08),
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.success.main, 0.3),
            }}
          >
            <Typography sx={{ fontWeight: 700, mb: 1 }}>
              {en ? 'The link is ready' : 'Ссылка готова'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                value={abs(fresh.url)}
                InputProps={{ readOnly: true }}
                onFocus={(e) => e.target.select()}
                sx={{ flex: 1, minWidth: 220 }}
              />
              <Button
                variant="contained"
                startIcon={
                  copied === fresh.url ? <CheckIcon /> : <ContentCopyIcon />
                }
                onClick={() => copy(fresh.url)}
              >
                {copied === fresh.url
                  ? en
                    ? 'Copied'
                    : 'Скопировано'
                  : en
                    ? 'Copy'
                    : 'Копировать'}
              </Button>
              <Button
                startIcon={<IosShareIcon />}
                onClick={() => shareOut(fresh.url)}
              >
                {en ? 'Send' : 'Отправить'}
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <TextField
            size="small"
            label={
              en ? 'For whom (only you see it)' : 'Для кого (видите только вы)'
            }
            placeholder={en ? 'e.g. Askar’s mum' : 'например: маме Аскара'}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            inputProps={{ maxLength: 80 }}
          />
          {videos.length > 0 ? (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={withVideo}
                    onChange={(e) => setWithVideo(e.target.checked)}
                  />
                }
                label={
                  en
                    ? `With the video (${videos.length})`
                    : `С видео (${videos.length})`
                }
              />
              {withVideo && videos.length > 1 && (
                <Box sx={{ pl: 5, display: 'grid' }}>
                  {videos.map((v) => {
                    const id = String(v._id);
                    return (
                      <FormControlLabel
                        key={id}
                        control={
                          <Checkbox
                            size="small"
                            checked={picked.includes(id)}
                            onChange={(e) =>
                              setPicked((p) =>
                                e.target.checked
                                  ? [...p, id]
                                  : p.filter((x) => x !== id)
                              )
                            }
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: '0.88rem' }}>
                            {v.title || (en ? 'Video' : 'Видео')}
                            {v.durationSec
                              ? ` · ${Math.floor(v.durationSec / 60)}:${String(v.durationSec % 60).padStart(2, '0')}`
                              : ''}
                          </Typography>
                        }
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          ) : (
            !loading && (
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                <VideocamOutlinedIcon fontSize="small" />
                {en
                  ? 'This assessment has no video'
                  : 'К этой оценке видео не записано'}
              </Typography>
            )
          )}
          <FormControlLabel
            control={
              <Switch
                checked={comments}
                onChange={(e) => setComments(e.target.checked)}
              />
            }
            label={
              en
                ? 'Teacher’s comments and notes'
                : 'Комментарии и заметки учителя'
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={fullName}
                onChange={(e) => setFullName(e.target.checked)}
              />
            }
            label={
              en
                ? 'Full name (otherwise the first name)'
                : 'Полное имя (иначе только имя)'
            }
          />
          <TextField
            select
            size="small"
            label={en ? 'Works for' : 'Действует'}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            sx={{ maxWidth: 220 }}
          >
            {EXPIRY.map(([d, ru, enl]) => (
              <MenuItem key={d} value={d}>
                {en ? enl : ru}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Typography sx={{ fontWeight: 800, mt: 3, mb: 1 }}>
          {en ? 'Links of this assessment' : 'Ссылки на эту оценку'}
          {active.length ? ` · ${active.length}` : ''}
        </Typography>
        {loading ? (
          <CircularProgress size={22} />
        ) : !links.length ? (
          <Typography sx={{ fontSize: '0.88rem', color: 'text.secondary' }}>
            {en ? 'No links yet' : 'Ссылок пока нет'}
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gap: 0.75 }}>
            {links.map((l) => (
              <Box
                key={l._id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  pl: 1.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  opacity: l.status === 'on' ? 1 : 0.55,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{ fontWeight: 700, fontSize: '0.9rem' }}
                  >
                    {l.label || (en ? 'Link' : 'Ссылка')}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{ fontSize: '0.78rem', color: 'text.secondary' }}
                  >
                    {[
                      l.status === 'on'
                        ? l.expiresAt
                          ? `${en ? 'until' : 'до'} ${fmt(l.expiresAt)}`
                          : en
                            ? 'no expiry'
                            : 'без срока'
                        : l.status === 'expired'
                          ? en
                            ? 'expired'
                            : 'истекла'
                          : en
                            ? 'switched off'
                            : 'выключена',
                      l.videos === 'none'
                        ? en
                          ? 'no video'
                          : 'без видео'
                        : en
                          ? 'with video'
                          : 'с видео',
                      `${en ? 'views' : 'просмотров'}: ${l.views}`,
                    ].join(' · ')}
                  </Typography>
                </Box>
                {l.status === 'on' && (
                  <>
                    <Tooltip title={en ? 'Copy' : 'Копировать'}>
                      <IconButton size="small" onClick={() => copy(l.url)}>
                        {copied === l.url ? (
                          <CheckIcon fontSize="small" />
                        ) : (
                          <ContentCopyIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={en ? 'Open' : 'Открыть'}>
                      <IconButton
                        size="small"
                        component="a"
                        href={l.url}
                        target="_blank"
                        rel="noopener"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={en ? 'Switch off' : 'Выключить'}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => revoke(l._id)}
                      >
                        <LinkOffIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button component={RouterLink} to="/links" onClick={onClose}>
          {en ? 'All public links' : 'Все публичные ссылки'}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>{en ? 'Close' : 'Закрыть'}</Button>
          <Button variant="contained" onClick={create} disabled={busy || !pid}>
            {busy
              ? en
                ? 'Creating…'
                : 'Создаю…'
              : en
                ? 'Create link'
                : 'Создать ссылку'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
